import { gptStore } from "@/store/gptStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import { isVideoBidder, isVideoSlotConfig } from "@/ts/guards";
import type {
	AdServerTargeting,
	AdUnit,
	AdUnits,
	AppNexusVideoBidderParams,
	Bid,
	CriteoVideoBidderParams,
	DisplayBidderData,
	IXVideoBidderParams,
	IXVideoParams,
	MSFTVideoBidderParams,
	PageMiscData,
} from "@/ts/prebid/types";
import type { DisplaySlotConfig, VideoSlotConfig } from "@/ts/types";

// Add display bidder data
export const addDisplayBidderDataToBids = (
	bids: Bid[] = [],
	slotConfig: DisplaySlotConfig,
	pageMisc: PageMiscData,
) => {
	const displayBidderData: DisplayBidderData = {
		ozone: {
			customData: [
				{
					settings: {},
					targeting: {
						slotName: slotConfig.slotId,
						hivis: slotConfig.targeting.hivis,
						...pageMisc,
					},
				},
			],
		},
	};

	return bids.map((bid) => {
		const updatedBid = { ...bid };
		const { bidder, params } = bid;
		if (bidder === "ozone") {
			updatedBid.params = {
				...params,
				...displayBidderData[bidder],
			};
		} else if (bidder === "ix" && isVideoBidder(updatedBid.params)) {
			return addRequiredIxVideoBidderParams(updatedBid);
		}
		return updatedBid;
	});
};

// Add video bidder data
export const addVideoBidderDataToBids = (
	bids: Bid[],
	videoSlotConfig: VideoSlotConfig,
) => {
	const playback_method = videoSlotConfig.isAutoPlay
		? "auto_play_sound_off"
		: "click_to_play";
	const playbackmethod = videoSlotConfig.isAutoPlay ? [2] : [3]; // as per the OpenRTB 2.5 Spec
	const videoBidderData = {
		appnexus: {
			keywords: {
				mantis: videoSlotConfig.mantisData?.mantis.split(","),
				mantis_context: videoSlotConfig.mantisData?.mantisContext.split(","),
			},
			video: {
				playback_method,
			},
		},
		criteo: {
			mediaTypes: {
				playerSize: videoSlotConfig.currentSize,
				playbackmethod,
			},
		},
		ix: {
			size: videoSlotConfig.currentSize,
			video: {
				w: videoSlotConfig.currentSize[0],
				h: videoSlotConfig.currentSize[1],
				playerSize: videoSlotConfig.currentSize,
				playbackmethod,
			},
		},
		msft: {
			keywords: {
				mantis: videoSlotConfig.mantisData?.mantis.split(","),
				mantis_context: videoSlotConfig.mantisData?.mantisContext.split(","),
			},
		},
	};

	return bids.map((bid) => {
		if (isVideoBidder(bid.params)) {
			switch (bid.bidder) {
				case "appnexus": {
					const appnexusParams = bid.params as AppNexusVideoBidderParams;
					return {
						...bid,
						params: {
							...appnexusParams,
							video: {
								...appnexusParams.video,
								...videoBidderData[bid.bidder].video,
							},
							keywords: videoBidderData[bid.bidder].keywords,
						},
					};
				}
				case "msft": {
					const msftParams = bid.params as MSFTVideoBidderParams;
					return {
						...bid,
						params: {
							...msftParams,
							keywords: videoBidderData[bid.bidder].keywords,
						},
					};
				}
				case "criteo": {
					const criteoParams = bid.params as CriteoVideoBidderParams;
					return {
						...bid,
						params: {
							...criteoParams,
							mediaTypes: {
								...criteoParams.mediaTypes,
								...videoBidderData[bid.bidder].mediaTypes,
							},
						},
					};
				}
				case "ix": {
					const ixParams = bid.params as IXVideoBidderParams;
					return addRequiredIxVideoBidderParams({
						...bid,
						params: {
							...ixParams,
							size: videoBidderData[bid.bidder].size,
							video: {
								...ixParams.video,
								...videoBidderData[bid.bidder].video,
							},
						},
					});
				}
				default: {
					return bid;
				}
			}
		}
		return bid;
	});
};

export const addRequiredIxVideoBidderParams = (bid: Bid) => {
	if (!isVideoBidder(bid.params)) {
		return bid;
	}
	const ixParams = bid.params as IXVideoBidderParams;
	const playerSize = ixParams.video.playerSize || [640, 360];
	// default video params has default player size ensure correct size is passed via breakpoints in the respective json
	const defaultVideoParams: IXVideoParams = {
		w: playerSize[0],
		h: playerSize[1],
		playerSize: playerSize,
		playbackmethod: [2],
		plcmt: 4,
		context: "instream", //defaults to instream videos need to set the context.
		mimes: ["video/mp4", "video/webm", "application/javascript"],
		skippable: false,
		minduration: 0,
		maxduration: 30,
		protocols: [1, 2, 3, 4, 5, 6],
	};
	return {
		...bid,
		params: {
			...ixParams,
			video: {
				...defaultVideoParams,
				...(ixParams.video || {}),
			},
		},
	};
};

// Add video player size
export const addVideoMediaTypes = (
	adUnit: AdUnit,
	videoSlotConfig: VideoSlotConfig,
) => ({
	...adUnit.mediaTypes,
	video: {
		...adUnit.mediaTypes.video,
		playerSize: videoSlotConfig.currentSize,
	},
});

// Add `ortb2Imp`
export const addOrtb2Imp = (
	slotConfig: VideoSlotConfig | DisplaySlotConfig,
	adUnit: AdUnit,
) => {
	const isVideoSlot = isVideoSlotConfig(slotConfig);
	const pageAdUnitPath = gptStore.getState().pageAdUnitPath;
	const adUnitUrl = isVideoSlot
		? (slotConfig.videoAdUnitPath ?? pageAdUnitPath)
		: (slotConfig.adUnitPath ?? pageAdUnitPath);
	return {
		ext: {
			gpid: `${adUnitUrl}#${adUnit.code}`,
			...(isVideoSlot && {
				data: {
					jwTargeting: {
						playerID: slotConfig.playerId,
						mediaID: slotConfig.videoId,
					},
				},
			}),
		},
	};
};

export const updatePrebidTargetingToStore = (
	auctionId: number,
	slotIds: string[],
	adServerTargeting: AdServerTargeting,
) => {
	const { actions } = headerBiddingStore.getState();
	// Filter out targeting from in previous auctions
	const targetingEntries = Object.entries(adServerTargeting).filter(
		([slotId, targeting]) =>
			slotIds.includes(slotId) && Object.keys(targeting).length > 0,
	);

	for (const [slotId, targeting] of targetingEntries) {
		actions.updateSlotTargeting(auctionId, slotId, targeting);
	}
};

// Filter out gumgum bidder from ad units
export const filterGumgumBidder = (adUnits: AdUnits): AdUnits => {
	const filteredAdUnits: AdUnits = {};

	for (const [key, adUnit] of Object.entries(adUnits)) {
		filteredAdUnits[key] = {
			...adUnit,
			bids: adUnit.bids.filter((bid) => bid.bidder !== "gumgum"),
		};
	}

	return filteredAdUnits;
};

export const getPageTempl = (pageMisc?: PageMiscData): string => {
	const pageType = pageMisc?.pageType?.toLowerCase() || "";
	if (pageType === "home") return "home";
	if (pageType.includes("article")) return "article";
	if (pageType.includes("live-event")) return "live-event";
	return "article";
};
